import { useEffect } from 'react';

import { Box, Flex, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';

import { LoomChoiceVariantA } from '@/components/signup/loom-land-opt-in/loom-choice-variant-a';
import { LoomChoiceVariantB } from '@/components/signup/loom-land-opt-in/loom-choice-variant-b';
import { useLoomLandOptIn } from '@/components/signup/loom-land-opt-in/use-loom-land-opt-in';
import {
	resolveLoomEdition,
	useTwcPrice,
} from '@/components/signup/loom-land-opt-in/use-twc-price';
import { useLocation } from '@/utils/location-provider';

/**
 * TWCG-2108 Loom Land opt-in choice page.
 *
 * Route: /try/cloud/loom/choose
 *
 * Branches on the `twcg2108LoomLandOptIn` multivariant experiment cohort:
 * - control:  fall through to the existing Loom-only signup flow.
 * - radio:    {@link LoomChoiceVariantA} (multi-option "TWC choice" card).
 * - longText: {@link LoomChoiceVariantB} (single-option TWC pitch).
 *
 * This screen is read-only w.r.t. the experiment: it only consumes the resolved
 * cohort to render the correct arm. Exposure is owned by `/try/cloud/signup`,
 * where the treatment (the continue-URL rewrite) applies to the whole Loom-intent
 * population, so this page does NOT fire exposure.
 */

const pageStyles = xcss({
	minHeight: '100vh',
	width: '100%',
	backgroundColor: 'elevation.surface',
});

const centeredStyles = xcss({
	minHeight: '100vh',
	padding: 'space.400',
});

const redirectToLoomOnlySignup = (search: string) => {
	// Control cohort keeps today's behaviour: the standard Loom signup page.
	window.location.replace(`/try/cloud/signup${search}`);
};

export const LoomChoicePageTemplate = () => {
	const { cohort, isResolved, isEnrolled } = useLoomLandOptIn();
	const location = useLocation();
	// Live, localized choice-card price for the Loom edition (from the `edition`
	// query param); `mode` picks the copy. Fetched client-side (no SSR pricing), so
	// we hold the spinner while loading rather than flash the default price.
	const edition = resolveLoomEdition(new URLSearchParams(location.search).get('edition'));
	const {
		price,
		mode: priceMode,
		isLoading: isPriceLoading,
		hasError: hasPriceError,
	} = useTwcPrice(edition);

	// Fall through to the Loom-only signup whenever we are not going to show a
	// treatment arm:
	//  - resolved control cohort, or
	//  - definitively not enrolled (indeterminate / not-enrolled — e.g. no Loom
	//    intent or Statsig failed to initialise), or
	//  - the selected edition is Enterprise, which has no monthly per-user rate,
	//    so there's no meaningful "how much more" to pitch — skip the screen, or
	//  - the price fetch failed or timed out: without a price the cards have
	//    nothing to pitch, so bail to the standard signup instead of spinning.
	// Only the transient "enrolled but arm not yet resolved" window renders
	// nothing, so users never get stuck on a blank page.
	const shouldRedirect =
		(isResolved && cohort === 'control') ||
		!isEnrolled ||
		edition === 'enterprise' ||
		hasPriceError;

	useEffect(() => {
		if (shouldRedirect) {
			redirectToLoomOnlySignup(location.search);
		}
	}, [shouldRedirect, location.search]);

	if (shouldRedirect || !isResolved) {
		// Redirecting (handled in the effect above), or still waiting for Statsig
		// to resolve the arm for an enrolled user; render nothing meanwhile.
		return null;
	}

	return (
		<Box xcss={pageStyles}>
			<Flex xcss={centeredStyles} alignItems="center" justifyContent="center">
				{/* Arm resolved. While the live price is still loading, show a spinner
				    in place of the cards so they don't flash the default price first. */}
				{isPriceLoading ? (
					<Spinner label="Loading" testId="loom-choice-price-spinner" />
				) : /* `'longText'` is the LoomChoiceVariantB cell of the
				      twcg-2108-loom-land-opt-in experiment; the three cell names
				      (control/radio/longText) match the Statsig console exactly —
				      see experiments.twcg2108LoomLandOptIn in the experiment manifest. */
				cohort === 'longText' ? (
					<LoomChoiceVariantB
						price={price}
						priceMode={priceMode}
						edition={edition}
						search={location.search}
					/>
				) : (
					<LoomChoiceVariantA
						price={price}
						priceMode={priceMode}
						edition={edition}
						search={location.search}
					/>
				)}
			</Flex>
		</Box>
	);
};
